// Lead time zones are stored as "standard/daylight" codes. Legacy data and the
// admin-managed Zone dropdown use other spellings ("Eastern Time", "EST",
// "pacific time"…) — everything is normalised to these values on save.
// Keep in sync with Backend/utility/leadTimeZone.js.
export const LEAD_TIME_ZONES = Object.freeze({
  EASTERN: "EST/EDT",
  CENTRAL: "CST/CDT",
  MOUNTAIN: "MST/MDT",
  PACIFIC: "PST/PDT",
  ALASKA: "AKST/AKDT",
  HAWAII: "HST", // Hawaii does not observe daylight saving time
});

// Whole-word tokens that identify each zone. Hawaii is checked first because
// its IANA id ("Pacific/Honolulu") also contains "pacific".
const ZONE_TOKENS = [
  [LEAD_TIME_ZONES.HAWAII, ["hawaii", "hst", "honolulu"]],
  [LEAD_TIME_ZONES.ALASKA, ["alaska", "akst", "akdt", "anchorage"]],
  [LEAD_TIME_ZONES.EASTERN, ["eastern", "est", "edt", "et", "york"]],
  [LEAD_TIME_ZONES.CENTRAL, ["central", "cst", "cdt", "ct", "chicago"]],
  [LEAD_TIME_ZONES.MOUNTAIN, ["mountain", "mst", "mdt", "mt", "denver"]],
  [LEAD_TIME_ZONES.PACIFIC, ["pacific", "pst", "pdt", "pt", "angeles"]],
];

/** Canonical code for a time zone value, or null when it isn't recognised. */
export const getTimeZoneCode = (value) => {
  if (typeof value !== "string") return null;
  const tokens = value.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  if (tokens.length === 0) return null;
  const match = ZONE_TOKENS.find(([, words]) => tokens.some((t) => words.includes(t)));
  return match ? match[0] : null;
};

/** Value to store for a lead: the canonical code, or the input unchanged if unrecognised. */
export const normalizeTimeZone = (value) => getTimeZoneCode(value) || value;

/**
 * Dropdown option value that represents the same zone as `value`, so a stored
 * "EST/EDT" still selects an option named e.g. "Eastern Time". Falls back to
 * `value` itself when nothing matches.
 */
export const findTimeZoneOption = (value, options = []) => {
  const code = getTimeZoneCode(value);
  if (!code) return value;
  const match = options.find((opt) => getTimeZoneCode(opt?.value) === code);
  return match ? match.value : value;
};
